import React, { useState } from 'react';
import { ExternalLink, Globe, Clock, ChevronDown, ChevronRight } from 'lucide-react';

const WebSearchProgress = React.memo(({ status, query, results = [], collapsed = true }) => {
  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  
  if (!status) return null;

  const getStatusDisplay = () => {
    switch (status) {
      case 'starting':
        return (
          <div className="flex items-center space-x-2 text-blue-600">
            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Preparing to search...</span>
          </div>
        );
      case 'searching':
        return (
          <div className="flex items-center space-x-2 text-blue-600">
            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Searching the web{query ? ` for: "${query}"` : '...'}</span>
          </div>
        );
      case 'results':
        return (
          <div className="space-y-3">
            <div 
              className="flex items-center space-x-2 text-green-600 cursor-pointer"
              onClick={() => setIsCollapsed(!isCollapsed)}
            >
              {isCollapsed ? (
                <ChevronRight className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              <Globe className="h-4 w-4" />
              <span className="font-medium">
                Search results{query ? ` for: "${query}"` : ''} ({results.length} results)
              </span>
            </div>
            {!isCollapsed && results.length > 0 && (
              <div className="space-y-2 ml-8">
                {results.map((result, index) => (
                  <div key={index} className="border rounded-lg p-3 bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                    <div className="flex items-start justify-between space-x-3">
                      <div className="flex-1 min-w-0">
                        <a 
                          href={result.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-medium truncate block"
                        >
                          {result.title}
                        </a>
                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                          {result.url}
                        </p>
                        {result.page_age && (
                          <div className="flex items-center space-x-1 mt-1">
                            <Clock className="h-3 w-3 text-gray-500" />
                            <span className="text-xs text-gray-500">
                              Updated: {result.page_age}
                            </span>
                          </div>
                        )}
                      </div>
                      <ExternalLink className="h-4 w-4 text-gray-400 flex-shrink-0" />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      case 'complete':
        return (
          <div className="flex items-center space-x-2 text-green-600">
            <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span>Search complete</span>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`rounded-lg p-3 mb-4 ${status === 'results' ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800' : 'inline-flex items-center px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20'} text-sm`}>
      {getStatusDisplay()}
    </div>
  );
});

export default WebSearchProgress;